/**
 * Agent Output Viewer Component
 * 
 * View the content packages forged by Hermes.
 * Displays all content with copy-to-clipboard functionality.
 */

import React, { useState, useEffect } from 'react';

// DEFENSIVE HELPER: Safely render any value as a string
const safeRender = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    // Handle nested objects with common fields
    if (value.title) return String(value.title);
    if (value.text) return String(value.text);
    if (value.name) return String(value.name);
    if (value.content) return String(value.content);
    try {
      return JSON.stringify(value);
    } catch {
      return '[Object]';
    }
  }
  return String(value);
};

// DEFENSIVE HELPER: Safely get array from data
const safeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return [value];
  return [];
};

const AgentOutputViewer = ({ API, agentId = null, outputId = null, packageData = null, reportData = null, agentType = null, onClose }) => {
  const [outputs, setOutputs] = useState([]);
  const [selectedOutput, setSelectedOutput] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('blog');
  const [copySuccess, setCopySuccess] = useState('');

  useEffect(() => {
    // If packageData is provided directly (Hermes/content_engine), use it
    if (packageData) {
      setSelectedOutput({ output_data: packageData, agent_type: 'content_engine' });
      setLoading(false);
    } 
    // If reportData is provided (Athena/competitor_spy), use it
    else if (reportData) {
      setSelectedOutput({ output_data: reportData, agent_type: 'competitor_spy' });
      setActiveTab('overview'); // Use different tabs for reports
      setLoading(false);
    }
    else if (outputId) {
      fetchSingleOutput(outputId);
    } else {
      fetchAllOutputs();
    }
  }, [outputId, packageData, reportData]);

  const fetchAllOutputs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/agents/outputs/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      let allOutputs = data.outputs || [];
      
      // Filter by agent type if specified, otherwise show content packages by default
      if (agentType) {
        allOutputs = allOutputs.filter(o => o.agent_type === agentType);
      } else {
        // Default: show content_engine outputs (Content Packages)
        // This ensures the "Content Packages" view shows actual content, not reports
        const contentOutputs = allOutputs.filter(o => o.agent_type === 'content_engine');
        if (contentOutputs.length > 0) {
          allOutputs = contentOutputs;
        }
      }
      
      setOutputs(allOutputs);
      if (allOutputs.length > 0) {
        setSelectedOutput(allOutputs[0]);
      }
    } catch (err) {
      console.error('Failed to fetch outputs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleOutput = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/agents/outputs/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSelectedOutput(data.output);
    } catch (err) {
      console.error('Failed to fetch output:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  const pkg = selectedOutput?.output_data;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!selectedOutput) {
    return (
      <div className="text-center py-20">
        <div className="text-6xl mb-4">📦</div>
        <h3 className="text-xl font-semibold text-gray-800">No Content Packages Yet</h3>
        <p className="text-gray-600">Run your agent to create your first content package!</p>
      </div>
    );
  }

  // Render competitor spy (Athena) report
  if (selectedOutput?.agent_type === 'competitor_spy' || reportData) {
    const report = selectedOutput?.output_data || reportData;
    return (
      <div className="bg-gray-900 rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 p-6 text-white">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🦉</span>
                <h2 className="text-xl font-bold">Intelligence Report</h2>
              </div>
              <p className="text-amber-100 text-lg">Athena&apos;s Competitive Analysis</p>
              <p className="text-amber-200 text-sm mt-1">
                Analyzed {report?.competitors_analyzed || 0} competitors • {report?.content_gaps?.gaps?.length || 0} gaps identified
              </p>
            </div>
            {onClose && (
              <button onClick={onClose} className="text-white/80 hover:text-white text-2xl">×</button>
            )}
          </div>
        </div>

        {/* Report Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-900 text-white">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-gray-800 rounded-xl border border-amber-500/30">
              <div className="text-3xl font-bold text-amber-400">{report?.competitors_analyzed || 0}</div>
              <div className="text-sm text-gray-400">Competitors</div>
            </div>
            <div className="text-center p-4 bg-gray-800 rounded-xl border border-orange-500/30">
              <div className="text-3xl font-bold text-orange-400">{report?.content_gaps?.gaps?.length || 0}</div>
              <div className="text-sm text-gray-400">Content Gaps</div>
            </div>
            <div className="text-center p-4 bg-gray-800 rounded-xl border border-emerald-500/30">
              <div className="text-3xl font-bold text-emerald-400">
                {report?.opportunities?.ranked_opportunities?.length || report?.topic_recommendations?.length || 0}
              </div>
              <div className="text-sm text-gray-400">Opportunities</div>
            </div>
          </div>

          {/* Top Gap */}
          {report?.top_gap && (
            <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl p-4 mb-6 border border-amber-500/30">
              <p className="text-xs text-amber-500 font-semibold mb-1">🎯 TOP OPPORTUNITY</p>
              <p className="text-white font-medium">
                {typeof report.top_gap === 'string' 
                  ? report.top_gap 
                  : (report.top_gap?.title || report.top_gap?.topic || report.top_gap?.reason || 'Strategic opportunity identified')}
              </p>
            </div>
          )}

          {/* Content Gaps */}
          {report?.content_gaps?.gaps?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-amber-400 mb-3">📋 Content Gaps</h3>
              <div className="space-y-2">
                {report.content_gaps.gaps.map((gap, i) => (
                  <div key={i} className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                    <p className="text-white">
                      {typeof gap === 'string' ? gap : (gap?.topic || gap?.title || gap?.description || JSON.stringify(gap))}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Opportunities */}
          {(report?.opportunities?.ranked_opportunities?.length > 0 || report?.topic_recommendations?.length > 0) && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-emerald-400 mb-3">💡 Opportunities</h3>
              <div className="space-y-2">
                {(report?.opportunities?.ranked_opportunities || report?.topic_recommendations || []).map((opp, i) => (
                  <div key={i} className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                    <p className="text-white font-medium">
                      {typeof opp === 'string' ? opp : (opp?.title || opp?.topic || opp?.recommendation || JSON.stringify(opp))}
                    </p>
                    {opp?.reason && <p className="text-gray-400 text-sm mt-1">{opp.reason}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Competitor Themes */}
          {report?.content_themes?.common_themes?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-purple-400 mb-3">🎯 Common Themes</h3>
              <div className="flex flex-wrap gap-2">
                {report.content_themes.common_themes.map((theme, i) => (
                  <span key={i} className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">
                    {typeof theme === 'string' ? theme : (theme?.name || theme?.topic || JSON.stringify(theme))}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">📦</span>
              <h2 className="text-xl font-bold">Content Package</h2>
            </div>
            <p className="text-purple-100 text-lg">{pkg?.topic}</p>
            <p className="text-purple-200 text-sm mt-1">
              Created: {pkg?.created_at ? new Date(pkg.created_at).toLocaleString() : 'Just now'}
              {' • '}{pkg?.credits_used || 0} credits • {Math.round(pkg?.execution_time_seconds || 0)}s
            </p>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-white/80 hover:text-white text-2xl">
              ×
            </button>
          )}
        </div>
      </div>

      {/* Copy Success Toast */}
      {copySuccess && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-fade-in">
          ✓ {copySuccess} copied!
        </div>
      )}

      {/* Tabs */}
      <div className="border-b bg-gray-50 px-4">
        <div className="flex gap-1 overflow-x-auto">
          {[
            { id: 'blog', label: '📄 Blog Post', badge: pkg?.blog_post?.seo_score ? `SEO: ${pkg.blog_post.seo_score}` : null },
            { id: 'images', label: '🎨 Images', badge: pkg?.featured_image ? '✨ NEW' : null },
            { id: 'social', label: '📱 Social Media', badge: Object.keys(pkg?.social_media || {}).length },
            { id: 'email', label: '📧 Email', badge: pkg?.email_newsletter?.subject_lines?.length },
            { id: 'hashtags', label: '#️⃣ Hashtags', badge: Object.values(pkg?.hashtags_by_platform || {}).flat().length },
            { id: 'prediction', label: '📊 Prediction', badge: `${pkg?.performance_prediction?.viral_probability}%` },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium transition whitespace-nowrap flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-white'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {tab.label}
              {tab.badge && (
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Blog Post Tab */}
        {activeTab === 'blog' && pkg?.blog_post && (
          <div className="space-y-6">
            {/* Headlines A/B */}
            {pkg.blog_post.headlines_ab?.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">🧪 A/B Headline Options</h3>
                <div className="space-y-2">
                  {safeArray(pkg.blog_post.headlines_ab).map((headline, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                      <span className="text-purple-600 font-mono">#{idx + 1}</span>
                      <span className="flex-1">{safeRender(headline)}</span>
                      <button
                        onClick={() => copyToClipboard(headline, `Headline ${idx + 1}`)}
                        className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                      >
                        Copy
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Main Content */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-700">📝 Blog Post ({pkg.blog_post.word_count} words)</h3>
                <button
                  onClick={() => copyToClipboard(pkg.blog_post.content, 'Blog post')}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  📋 Copy Full Post
                </button>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 prose max-w-none">
                <h1>{pkg.blog_post.title}</h1>
                <div dangerouslySetInnerHTML={{ __html: pkg.blog_post.content.replace(/\n/g, '<br/>') }} />
              </div>
            </div>

            {/* SEO Info */}
            {pkg.blog_post.meta_description && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">🎯 SEO Details</h3>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-green-700">Meta Description</span>
                    <button
                      onClick={() => copyToClipboard(pkg.blog_post.meta_description, 'Meta description')}
                      className="text-sm text-green-600 hover:text-green-800"
                    >
                      Copy
                    </button>
                  </div>
                  <p className="text-gray-700">{pkg.blog_post.meta_description}</p>
                  
                  {pkg.blog_post.keywords?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <span className="text-green-700 text-sm">Keywords: </span>
                      {safeArray(pkg.blog_post.keywords).map((kw, idx) => (
                        <span key={idx} className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded mr-2 mb-1 text-sm">
                          {safeRender(kw)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Images Tab */}
        {activeTab === 'images' && (
          <div className="space-y-8">
            {/* Featured Blog Image */}
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                🎨 Featured Blog Image
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">AI Generated</span>
              </h3>
              
              {pkg?.featured_image?.image_base64 ? (
                <div className="space-y-4">
                  <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200">
                    <img 
                      src={`data:image/png;base64,${pkg.featured_image.image_base64}`}
                      alt="AI Generated Featured Image"
                      className="w-full h-auto"
                    />
                  </div>
                  <div className="flex gap-3">
                    <a
                      href={`data:image/png;base64,${pkg.featured_image.image_base64}`}
                      download={`featured-image-${pkg.topic?.replace(/\s+/g, '-')}.png`}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                    >
                      ⬇️ Download Image
                    </a>
                    <button
                      onClick={() => copyToClipboard(pkg.featured_image.image_base64, 'Image data')}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      📋 Copy Base64
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-100 rounded-xl p-8 text-center">
                  <span className="text-4xl">🖼️</span>
                  <p className="text-gray-500 mt-2">No featured image generated for this package</p>
                </div>
              )}
            </div>

            {/* Social Media Graphics */}
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                🖼️ Social Media Graphics
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">Platform-Optimized</span>
              </h3>
              
              {pkg?.social_graphics && Object.keys(pkg.social_graphics).length > 0 ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {Object.entries(pkg.social_graphics).map(([platform, graphic]) => (
                    <div key={platform} className="border rounded-xl overflow-hidden">
                      <div className="bg-gray-100 px-4 py-2 flex items-center gap-2">
                        <span className="text-xl">
                          {{
                            linkedin: '💼',
                            twitter: '🐦',
                            instagram: '📸',
                            facebook: '📘'
                          }[platform] || '📱'}
                        </span>
                        <span className="font-semibold capitalize">{platform}</span>
                      </div>
                      <div className="p-2">
                        <img 
                          src={`data:image/png;base64,${graphic.image_base64}`}
                          alt={`${platform} graphic`}
                          className="w-full h-auto rounded"
                        />
                      </div>
                      <div className="p-3 bg-gray-50 border-t">
                        <a
                          href={`data:image/png;base64,${graphic.image_base64}`}
                          download={`${platform}-graphic-${pkg.topic?.replace(/\s+/g, '-')}.png`}
                          className="block w-full text-center px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                        >
                          ⬇️ Download for {platform.charAt(0).toUpperCase() + platform.slice(1)}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-100 rounded-xl p-8 text-center">
                  <span className="text-4xl">📱</span>
                  <p className="text-gray-500 mt-2">No social graphics generated for this package</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Social Media Tab */}
        {activeTab === 'social' && pkg?.social_media && (
          <div className="space-y-6">
            {Object.entries(pkg.social_media).map(([platform, content]) => (
              <div key={platform} className="border rounded-xl overflow-hidden">
                <div className="bg-gray-100 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">
                      {{
                        linkedin: '💼',
                        twitter: '🐦',
                        instagram: '📸',
                        facebook: '📘',
                        tiktok: '🎵'
                      }[platform] || '📱'}
                    </span>
                    <span className="font-semibold capitalize">{platform}</span>
                    <span className="text-sm text-gray-500">({content.format_type})</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(content.content, `${platform} content`)}
                    className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                  >
                    Copy
                  </button>
                </div>
                <div className="p-4">
                  <p className="whitespace-pre-wrap">{content.content}</p>
                  {content.hashtags?.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-sm text-gray-500 mb-2">Hashtags:</p>
                      <p className="text-purple-600">{content.hashtags.join(' ')}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Email Tab */}
        {activeTab === 'email' && pkg?.email_newsletter && (
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-700 mb-3">📧 Subject Line Options</h3>
              <div className="space-y-2">
                {pkg.email_newsletter.subject_lines?.map((subject, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                    <span className="text-purple-600 font-mono">#{idx + 1}</span>
                    <span className="flex-1 font-medium">{subject}</span>
                    <button
                      onClick={() => copyToClipboard(subject, `Subject line ${idx + 1}`)}
                      className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                    >
                      Copy
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {pkg.email_newsletter.preview_text && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Preview Text</h3>
                <div className="bg-gray-50 p-3 rounded-lg flex items-center justify-between">
                  <span>{pkg.email_newsletter.preview_text}</span>
                  <button
                    onClick={() => copyToClipboard(pkg.email_newsletter.preview_text, 'Preview text')}
                    className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-700">Email Body</h3>
                <button
                  onClick={() => copyToClipboard(pkg.email_newsletter.email_body, 'Email body')}
                  className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                >
                  Copy
                </button>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">
                {pkg.email_newsletter.email_body}
              </div>
            </div>
          </div>
        )}

        {/* Hashtags Tab */}
        {activeTab === 'hashtags' && pkg?.hashtags_by_platform && (
          <div className="space-y-6">
            {Object.entries(pkg.hashtags_by_platform).map(([platform, hashtags]) => (
              <div key={platform}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-700 capitalize">{platform}</h3>
                  <button
                    onClick={() => copyToClipboard(hashtags.join(' '), `${platform} hashtags`)}
                    className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                  >
                    Copy All
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {safeArray(hashtags).map((tag, idx) => (
                    <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                      {safeRender(tag)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Prediction Tab */}
        {activeTab === 'prediction' && pkg?.performance_prediction && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-purple-700">
                  {pkg.performance_prediction.predicted_views_min.toLocaleString()}-{pkg.performance_prediction.predicted_views_max.toLocaleString()}
                </p>
                <p className="text-purple-600 text-sm">Predicted Views</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-blue-700">{pkg.performance_prediction.engagement_rate}%</p>
                <p className="text-blue-600 text-sm">Engagement Rate</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-green-700">{pkg.performance_prediction.viral_probability}%</p>
                <p className="text-green-600 text-sm">Viral Probability</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-amber-700">{pkg.performance_prediction.confidence_score}%</p>
                <p className="text-amber-600 text-sm">Confidence</p>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-3">⏰ Best Posting Times</h3>
              <div className="flex flex-wrap gap-2">
                {safeArray(pkg.performance_prediction.best_posting_times).map((time, idx) => (
                  <span key={idx} className="px-4 py-2 bg-green-100 text-green-700 rounded-lg">
                    {safeRender(time)}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-3">📈 Performance Factors</h3>
              <ul className="space-y-2">
                {safeArray(pkg.performance_prediction.performance_factors).map((factor, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    <span>{safeRender(factor)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentOutputViewer;
